import { useContext, useEffect, useState } from 'react'

import {
  Icon,
  TogetherDataTransforms,
  TogetherTypes,
} from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Plus } from '@hhgtech/icons/core'
import { AspectRatio, Box, BoxProps } from '@mantine/core'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { CarouselOrListingBlock } from 'components/molecules/carouselOrListingBlock'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  SpotlightCommunityWithPostEmbedType,
  SpotlightCommunityWithPostsSession,
} from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { CommunityBtn } from './communityBtn'
import useStyles from './index.styles'
import { Item, RelevantCardPostType } from './item'
import SectionWrapper from '../sectionWrapper'

type Props = {
  maxPosts?: number
  applyH1?: boolean
} & SpotlightCommunityWithPostsSession &
  BoxProps

const EmbedCommunityWithPosts = (props: Props) => {
  const {
    styles,
    classNames,
    maxPosts = 7,
    applyH1,
    title,
    description,
    typeEmbed,
    community,
    postsIds: postsIds = [],
    posts: postsProp = [],
    wrapperProps,
    ...boxProps
  } = props
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const [posts, setPosts] = useState<RelevantCardPostType[]>(postsProp)
  const [followIds, setFollowIds] = useState<string[]>([])
  const { classes, theme } = useStyles(undefined, {
    name: 'EmbedCommunityWithPosts',
    styles,
    classNames,
  })

  const isCommunity =
    typeEmbed === SpotlightCommunityWithPostEmbedType.COMMUNITY && community
  const isPost =
    typeEmbed === SpotlightCommunityWithPostEmbedType.POST && posts.length > 0

  useEffect(() => {
    ;((isPost && postsIds.length > 0) || (isCommunity && community?.id)) &&
      (async () => {
        const res = await callApiNew(
          PATHS.TOGETHER.GET_TOGETHER_POST_LIST,
          {
            method: 'GET',
            params:
              postsIds?.length > 0
                ? {
                    includeIds: postsIds.join(','),
                    pageSize: maxPosts,
                  }
                : {
                    communityId: community?.id,
                    startDate: dayjs()
                      .subtract(2, 'month')
                      .format('YYYY-MM-DD'),
                    endDate: dayjs().format('YYYY-MM-DD'),
                    pageSize: maxPosts,
                    sortBy: 'relevant',
                    postBy: 'member',
                  },
          },
          'together',
        )
        setPosts(
          (res?.data?.posts || []).map(TogetherDataTransforms.formatPost),
        )
      })()
  }, [postsIds])

  useEffect(() => {
    isLoggedIn &&
      fetchCommunityMember(
        isCommunity
          ? String(community?.id)
          : postsProp
              .map((i) => i?.community?.id)
              .filter(Boolean)
              .join(','),
      )
  }, [isLoggedIn])

  useEffect(() => {
    setPosts(
      posts.map((i) => {
        return {
          ...i,
          joinedCommunity: followIds.includes(String(i?.community?.id)),
        }
      }),
    )
  }, [followIds])

  const joinCommunity = async (id: string | number) =>
    await callApiNew(
      PATHS.TOGETHER.POST_TOGETHER_JOIN_COMMUNITY,
      {
        method: 'POST',
        data: {
          ids: [id],
        },
      },
      'together',
    )

  const fetchCommunityMember = async (ids: string) => {
    if (!ids) return
    const communityRes = await callApiNew(
      PATHS.TOGETHER.GET_TOGETHER_COMMUNITY,
      {
        params: {
          includeIds: ids,
        },
      },
      'together',
    )
    setFollowIds(
      ((communityRes?.data?.communities || []) as TogetherTypes.CommunityApi[])
        .filter((i) => i.joined)
        .map((i) => String(i.id)),
    )
  }
  const embedCommunityFollowing = followIds?.[0] === String(community?.id)
  const communityAction =
    isCommunity && community?.id ? (
      <CommunityBtn
        isFollowing={embedCommunityFollowing}
        leftIcon={
          !embedCommunityFollowing ? <Plus color="#fff" size={18} /> : undefined
        }
        rightIcon={
          embedCommunityFollowing ? (
            <Icon.ArrowRightTail color="#fff" size={18} />
          ) : undefined
        }
        community={community}
        joinCommunity={joinCommunity}
        onJoinCommunity={() =>
          community?.id && setFollowIds([...followIds, String(community.id)])
        }
        joinedLabel={f(
          {
            id: 'cardCommunity.joinCommunityName',
          },
          {
            name: community?.name,
          },
        )}
        goToLabel={f(
          {
            id: 'cardCommunity.goToCommunityName',
          },
          {
            name: community?.name,
          },
        )}
      />
    ) : null

  return isCommunity || isPost ? (
    <SectionWrapper.Wrapper
      defaultColor={theme.colors.neutral[0]}
      {...wrapperProps}
      {...boxProps}
    >
      <Box className={classes.inner}>
        <Box className={classes.info}>
          <Box className={classes.infoLeft}>
            {isCommunity && (
              <Box className={classes.thumbnail}>
                <AspectRatio ratio={1} className={classes.thumbnailImg}>
                  <img
                    src={community.thumbnail}
                    alt={community.name}
                    loading="lazy"
                  />
                </AspectRatio>
              </Box>
            )}
            <Box className={classes.header}>
              {title && (
                <Text
                  size="h3"
                  as={applyH1 ? 'h1' : 'h2'}
                  className={classes.heading}
                >
                  {title}
                </Text>
              )}
              {description && <Text size="p3">{description}</Text>}
            </Box>
          </Box>
          {!isMobile && communityAction}
        </Box>
        <CarouselOrListingBlock lengthToCarouselDesktop={3}>
          <CarouselOrListingBlock.Slider>
            {posts.map((i) => (
              <Item
                key={i.id}
                post={i}
                jointCommunityBtn={
                  typeEmbed === SpotlightCommunityWithPostEmbedType.POST
                }
                joinCommunity={joinCommunity}
              />
            ))}
          </CarouselOrListingBlock.Slider>
        </CarouselOrListingBlock>
        {isMobile && communityAction}
      </Box>
    </SectionWrapper.Wrapper>
  ) : // <Box className={cx(classes.root, className)}>
  //   <Container>

  //   </Container>
  // </Box>
  null
}

export { EmbedCommunityWithPosts }
